import React from 'react';
import { Form } from 'react-bootstrap';

const CustomerDetails = ({
  customerDetails,
  foreCloseCharges,
  handleForeCloseChargesChange,
  foreCloseChargesAmount,
  foreCloseGST,
  totalPayableAmount,
  securityDeposit,
  setSecurityDeposit,
  recordedSecurityDeposit,
  isSecurityDepositAdjusted,
  setIsSecurityDepositAdjusted,
  netPayableAmount,
  reason,
  setReason,
  waiverReason,
  setWaiverReason,
  waiverAmount
}) => {
  const readOnlyDeposit =
    customerDetails.isReadOnly || customerDetails.branchManagerStatus === "submitted";

  // A warning only - submitting is still allowed. Shown once a value is typed.
  const charges = parseFloat(foreCloseCharges);
  const chargesWarning =
    String(foreCloseCharges ?? "").trim() === "" || !Number.isFinite(charges)
      ? null
      : charges === 0
        ? "Warning: 0% charge - no foreclosure charge will be collected. Please check."
        : charges > 20
          ? `Warning: ${charges}% is a very high charge. Please check before submitting.`
          : null;

  return (
    <div className="foreclosure-card h-100">
      {customerDetails.isReadOnly && customerDetails.approvalStatus && (
        <div className="alert alert-info mb-3 py-2 px-3" role="alert" style={{ fontSize: '0.85rem' }}>
          {customerDetails.approvalStatus}
        </div>
      )}

      <h5 className="foreclosure-card-title">Customer Details & Settlement</h5>
      
      <table className="foreclosure-table mb-3">
        <tbody>
          <tr>
            <td width="50%" className="text-start fw-semibold" style={{ backgroundColor: 'var(--slate-50)' }}>Customer Name</td>
            <td className="text-start fw-bold" style={{ color: 'var(--text-main)' }}>{customerDetails.memberName}</td>
          </tr>
          <tr>
            <td className="text-start fw-semibold" style={{ backgroundColor: 'var(--slate-50)' }}>Total Outstanding</td>
            <td className="text-start fw-bold">₹{customerDetails.outstandingPrincipal}</td>
          </tr>
          <tr>
            <td className="text-start fw-semibold" style={{ backgroundColor: 'var(--slate-50)' }}>
              Foreclosure Charges (%) <span style={{ color: '#9E2B25' }}>*</span>
            </td>
            <td className="text-start">
              <Form.Control
                type="number"
                value={foreCloseCharges ?? ""}
                onChange={handleForeCloseChargesChange}
                readOnly={customerDetails.isReadOnly || customerDetails.branchManagerStatus === "submitted"}
                min="0"
                step="0.01"
                required
                style={{ maxWidth: "120px", display: "inline-block", height: "30px", fontSize: "0.85rem" }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.preventDefault()}
                onFocus={(e) => {
                  e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false });
                }}
                onBlur={(e) => {
                  e.target.removeEventListener("wheel", (e) => e.preventDefault());
                }}
              />
              {chargesWarning && (
                <div role="alert" style={{ color: '#9A6700', fontSize: '0.78rem', marginTop: '4px' }}>
                  {chargesWarning}
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td className="text-start fw-semibold" style={{ backgroundColor: 'var(--slate-50)' }}>Foreclose Charges Amount</td>
            <td className="text-start">₹{foreCloseChargesAmount}</td>
          </tr>
          <tr>
            <td className="text-start fw-semibold" style={{ backgroundColor: 'var(--slate-50)' }}>Foreclose Amount GST (18%)</td>
            <td className="text-start">₹{foreCloseGST}</td>
          </tr>
          <tr style={{ backgroundColor: '#F0F5FF' }}>
            <td className="text-start fw-bold" style={{ color: 'var(--primary-brand)' }}>Total Payable / Demand</td>
            <td className="text-start fw-bold" style={{ color: 'var(--primary-brand)', fontSize: '1rem' }}>₹{totalPayableAmount}</td>
          </tr>
          <tr>
            <td className="text-start fw-semibold" style={{ backgroundColor: 'var(--slate-50)' }}>
              Security Deposit
              {recordedSecurityDeposit != null && (
                <span className="fw-normal" style={{ color: 'var(--text-muted, #64748b)' }}> (₹{recordedSecurityDeposit})</span>
              )}{' '}
              <span style={{ color: '#9E2B25' }}>*</span>
            </td>
            <td className="text-start">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* The deposit actually collected from the customer. Required; 0 is allowed. */}
                <Form.Control
                  type="number"
                  value={securityDeposit ?? ""}
                  onChange={(e) => setSecurityDeposit(e.target.value)}
                  readOnly={readOnlyDeposit}
                  min="0"
                  step="1"
                  required
                  aria-label="Security deposit collected"
                  style={{ maxWidth: "120px", display: "inline-block", height: "30px", fontSize: "0.85rem" }}
                  onKeyDown={(e) => {
                    if (["ArrowUp", "ArrowDown", "-", "+", "e", "E", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                />
                <Form.Check
                  type="switch"
                  id="security-deposit-adjusted"
                  checked={isSecurityDepositAdjusted}
                  onChange={(e) => setIsSecurityDepositAdjusted(e.target.checked)}
                  disabled={readOnlyDeposit}
                  label={isSecurityDepositAdjusted ? "Returned (deducted)" : "Not returned"}
                  style={{ fontSize: "0.82rem", marginBottom: 0 }}
                />
              </div>
            </td>
          </tr>
          <tr style={{ backgroundColor: '#F0F5FF' }}>
            <td className="text-start fw-bold" style={{ color: 'var(--primary-brand)' }}>Net Payable</td>
            <td className="text-start fw-bold" style={{ color: 'var(--primary-brand)', fontSize: '1rem' }}>₹{netPayableAmount}</td>
          </tr>
        </tbody>
      </table>

      <Form.Group className="mt-auto">
        <Form.Label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
          Reason / Remarks:
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          readOnly={customerDetails.isReadOnly}
          placeholder="Enter reason for foreclosure..."
          style={{ fontSize: "0.85rem" }}
        />
      </Form.Group>
      {/* Kept separate from the foreclosure reason on purpose: "why the loan is
          being closed" and "why money was written off" are different questions,
          and an auditor reviewing a waiver needs the second one specifically. */}
      {waiverAmount > 0 && (
        <Form.Group className="mt-3">
          <Form.Label style={{ fontSize: "0.85rem", fontWeight: "600", color: "#9E2B25", marginBottom: "4px" }}>
            Waiver Reason (required):
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={waiverReason}
            onChange={(e) => setWaiverReason(e.target.value)}
            readOnly={customerDetails.isReadOnly}
            placeholder={`Why is Rs.${waiverAmount} being waived?`}
            style={{ fontSize: "0.85rem", borderColor: "#9E2B25" }}
          />
        </Form.Group>
      )}
    </div>
  );
};

export default CustomerDetails;